import { type Cents, type Minor, cents, minor, formatMinor, parseMinor, toInputString } from "@/domain/money";
export { isoWithOffset as splitInstant } from "@/domain/period";
import { BASIS_POINTS_TOTAL, type SplitMode, type SplitValidationReason } from "../../../shared/split_rules";

/** Currency changes preserve typed major units; fractional JPY is refused, never rounded. */
export function captureSplitAmount(amount: Cents, currency: string): Minor {
  return parseMinor(toInputString(amount).replace(/\.?0+$/, ""), currency);
}

export function parseEntry(text: string, mode: SplitMode, currency: string): number {
  if (mode === "exact") return parseMinor(text, currency);
  if (mode === "percent") return parseMinor(text, "SGD");
  if (!/^\d+$/.test(text)) throw new Error("Enter a whole number of shares.");
  const value = Number(text);
  if (!Number.isSafeInteger(value)) throw new Error("Enter a smaller whole number of shares.");
  return value;
}

/** Draft hints identify the field that needs attention, without exposing row ids. */
export function parseSplitField<T>(label: string, parse: () => T): T {
  try { return parse(); }
  catch (cause) {
    if (!(cause instanceof Error) || cause instanceof TypeError) throw cause;
    const decimals = /at most (\d+) decimal/.exec(cause.message);
    throw new Error(decimals ? `${label} can have at most ${decimals[1]} decimals` : `${label}: ${cause.message}`, { cause });
  }
}

export function splitValidationCopy(reason: SplitValidationReason, input: { amountMinor: number; currency: string; entries: readonly { memberId: string; value: number }[] }): string {
  if (reason === "exact_total" || reason === "percent_total") {
    const total = reason === "percent_total" ? BASIS_POINTS_TOTAL : input.amountMinor;
    const remaining = total - input.entries.reduce((sum, entry) => sum + entry.value, 0);
    const display = (value: number) => reason === "percent_total" ? `${toInputString(cents(value)).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")}%` : formatMinor(minor(value), input.currency);
    return `${reason === "percent_total" ? "Percentages" : "Exact amounts"} must add up to ${display(total)} — ${display(Math.abs(remaining))} ${remaining < 0 ? "too much" : "left"}`;
  }
  const copy = {
    invalid_mode: "Choose Even, Shares, Exact or %.", invalid_currency: "Choose a supported currency.", invalid_amount: "Enter the amount", amount_too_large: "Reduce the amount or entry to the allowed maximum.",
    unknown_member: "Choose a member of this group.", duplicate_member: "Include each member only once.", invalid_value: "Enter a non-negative amount for each member.", invalid_even_value: "Select the members sharing this expense.",
    no_participants: "Select at least one participant.", zero_shares: "Enter at least one positive share weight.",
  };
  return copy[reason];
}
